// Project-block commands (timeBlocks/{id}: a PROJECT placed on the calendar — hours of a day, or a job day).
// A block always has a project: it is what pulls that project's open outline into the rolling plan
// (dayplan.js projectsOnToday). Time set aside with someone / somewhere is an EVENT, not a block. Pure.
import { del, set, update } from './writes.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const DATE = /^\d{4}-\d{2}-\d{2}$/;
export const BLOCK_KINDS = ['work', 'job-day'];

/** Minutes after midnight [start, end] from a block, new fields first, legacy hour/minute fields otherwise. */
export function blockMinutes(b) {
  if (!b) return [0, 0];
  const start = Number.isFinite(b.start) ? b.start : (Number(b.startHour) || 0) * 60 + (Number(b.startMinute) || 0);
  const end = Number.isFinite(b.end) ? b.end : (Number(b.endHour) || 0) * 60 + (Number(b.endMinute) || 0);
  return [start, end];
}

/** Both encodings of a [start, end] in minutes — old builds read the hour fields, dayplan orders by startHour. */
export function blockTime(start, end) {
  return { start, end, startHour: Math.floor(start / 60), startMinute: start % 60, endHour: Math.floor(end / 60), endMinute: end % 60 };
}

export function makeBlockDoc(block, at, newId, { project = null, department = null } = {}) {
  if (!block.projectId) throw new Error('a block is a project on the calendar — projectId required (use an event or a timed task otherwise)');
  if (!DATE.test(block.date || '')) throw new Error('date must be YYYY-MM-DD');
  let start, end;
  if (Number.isFinite(block.start) || Number.isFinite(block.end)) { start = Number(block.start); end = Number(block.end); }
  else { start = Number(block.startHour) * 60 + (Number(block.startMinute) || 0); end = Number(block.endHour) * 60 + (Number(block.endMinute) || 0); }
  if (!(start >= 0 && start < 1440 && end > 0 && end <= 1440 && end > start)) throw new Error('start/end must be within the day (0–24 h) with end after start');
  const kind = BLOCK_KINDS.includes(block.kind) ? block.kind : 'work';
  return {
    id: block.id || newId(), projectId: block.projectId, date: block.date, kind,
    title: block.title || project?.name || '',
    departmentId: block.departmentId ?? project?.departmentId ?? null,
    ...blockTime(start, end),
    color: block.color || department?.color || project?.color || '#3B82F6',
    ...(Array.isArray(block.taskIds) ? { taskIds: block.taskIds.filter(Boolean) } : {}),
    ...(block.repeat ? { repeat: block.repeat } : {}),
    ...(Array.isArray(block.crew) && block.crew.filter(Boolean).length ? { crew: [...new Set(block.crew.filter(Boolean))] } : {}),
    createdAt: at,
  };
}

/** ctx.projects / ctx.departments (optional) fill the label, department and colour. */
export function addBlock(ctx, input) {
  const project = (ctx.projects || []).find((p) => p.id === input.projectId) || null;
  if (input.projectId && ctx.projects && !project) throw new Error(`no project ${input.projectId}`);
  const department = project ? (ctx.departments || []).find((d) => d.id === project.departmentId) || null : null;
  const block = makeBlockDoc(input, now(ctx), ctx.newId, { project, department });
  return { writes: [set(`timeBlocks/${block.id}`, block)], block };
}

const B_EDITABLE = new Set(['title', 'projectId', 'date', 'start', 'end', 'startHour', 'startMinute', 'endHour', 'endMinute', 'kind', 'departmentId', 'color', 'taskIds', 'repeat', 'crew']);   // crew: [uid] on the job day (crew.js)

export function updateBlock(ctx, { id, ...patch }) {
  const cur = (ctx.blocks || []).find((b) => b.id === id);
  if (!cur) throw new Error(`no project block ${id}`);
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (B_EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (!Object.keys(data).length) throw new Error('nothing to update');
  if ('start' in data || 'end' in data) {
    const [cs, ce] = blockMinutes(cur);
    Object.assign(data, blockTime(Number.isFinite(data.start) ? data.start : cs, Number.isFinite(data.end) ? data.end : ce));
  } else if (['startHour', 'startMinute', 'endHour', 'endMinute'].some((k) => k in data)) {
    const m = { ...cur, ...data }; delete m.start; delete m.end;
    Object.assign(data, blockTime(...blockMinutes(m)));
  }
  makeBlockDoc({ ...cur, ...data }, 0, () => cur.id);   // validates the merged block
  return { writes: [update(`timeBlocks/${id}`, data)], patch: data };
}

/** Move a block to another day and/or time (minutes). */
export function moveBlock(ctx, { id, date, start, end }) {
  const cur = (ctx.blocks || []).find((b) => b.id === id);
  if (!cur) throw new Error(`no project block ${id}`);
  const [cs, ce] = blockMinutes(cur);
  const s = Number.isFinite(start) ? start : cs;
  const e = Number.isFinite(end) ? end : (Number.isFinite(start) ? start + (ce - cs) : ce);
  return updateBlock(ctx, { id, ...(date ? { date } : {}), start: s, end: e });
}

export function deleteBlock(ctx, { id }) {
  if (!(ctx.blocks || []).some((b) => b.id === id)) throw new Error(`no project block ${id}`);
  return { writes: [del(`timeBlocks/${id}`)] };
}
